import { GRADIENT_SELECTED } from "../../utils/constants";
import { useState, useEffect } from "react";

interface LocationTabProps {
  viewContinent: string | null;
  viewCountry: string | null;
  selectedCategories: string[];
  selectedSearchTags: string[];
  selectedContinents: string[];
  selectedCountries: string[];
  selectedCities: string[];
  selectedWorldMaps: boolean;
  getAvailableContinents: () => string[];
  getAvailableCountries: (continent: string) => string[];
  getAvailableCities: (country: string) => string[];
  getAggregatedContinentCount: (continent: string) => number;
  getAggregatedCountryCount: (country: string) => number;
  getAggregatedCityCount: (city: string) => number;
  getWorldMapsCount: () => number;
  toggleLocationBadge: (value: string, type: 'continent' | 'country' | 'city' | 'world') => void;
  switchToMapTab: () => void;
  locationTabViewLevel: 'continents' | 'countries' | 'cities';
}

export function LocationTab({
  viewContinent,
  viewCountry,
  selectedCategories,
  selectedSearchTags,
  selectedContinents,
  selectedCountries,
  selectedCities,
  selectedWorldMaps,
  getAvailableContinents,
  getAvailableCountries,
  getAvailableCities,
  getAggregatedContinentCount,
  getAggregatedCountryCount,
  getAggregatedCityCount,
  getWorldMapsCount,
  toggleLocationBadge,
  switchToMapTab,
  locationTabViewLevel,
}: LocationTabProps) {
  // Delay child button rendering to prevent flash during badge height changes
  const [showChildButtons, setShowChildButtons] = useState(true);

  useEffect(() => {
    // When drilling down to countries or cities, delay rendering by one frame
    // to allow badge height measurement to complete first
    if (locationTabViewLevel === 'countries' || locationTabViewLevel === 'cities') {
      setShowChildButtons(false);
      const rafId = requestAnimationFrame(() => {
        setShowChildButtons(true);
      });
      return () => cancelAnimationFrame(rafId);
    } else {
      setShowChildButtons(true);
    }
  }, [locationTabViewLevel]);

  return (
    <div className="flex flex-col-reverse items-center justify-start min-h-full">
      <div className="w-full max-w-md px-3 pb-1">
        
        {/* World Maps Button - show when at continents level (appears at top due to flex-col-reverse) */}
        {locationTabViewLevel === 'continents' && (selectedCategories.length > 0 || selectedSearchTags.length > 0) && (() => {
          const count = getWorldMapsCount();
          return count > 0 ? (
            <div className="flex flex-col items-center w-full">
              <button
                onClick={() => {
                  if (selectedWorldMaps) {
                    return;
                  }
                  toggleLocationBadge('world', 'world');
                }}
                className={`${
                  selectedWorldMaps
                    ? `${GRADIENT_SELECTED} border-[var(--accent-blue)]`
                    : `bg-white/7 border-white/10`
                } relative border ${
                  selectedWorldMaps
                    ? "" // No hover effect when already selected
                    : "hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]"
                } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-[15px] p-2 md:p-2.5 w-11/12`}
                aria-label="Select World Maps"
                aria-pressed={selectedWorldMaps}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className="text-white text-lg md:text-[22px] whitespace-nowrap">
                    World Maps
                  </span>
                </div>
              </button>
            </div>
          ) : null;
        })()}
        
        {/* Separator - show when World Maps button is visible */}
        {locationTabViewLevel === 'continents' && (selectedCategories.length > 0 || selectedSearchTags.length > 0) && getWorldMapsCount() > 0 && (
          <div className="w-full flex justify-center my-5">
            <div className="w-4/5 h-[2px] bg-gradient-to-r from-transparent via-[var(--accent-blue)]/40 to-transparent"></div>
          </div>
        )}
        
        {/* Continents level */}
        {locationTabViewLevel === 'continents' && (
          <>
            <div className="flex flex-col items-center gap-3 w-full">
              {getAvailableContinents().map((continent) => {
                return (
                  <button
                    key={continent}
                    onClick={() => toggleLocationBadge(continent, 'continent')}
                    className={`${
                      selectedContinents.includes(continent)
                        ? `${GRADIENT_SELECTED} border-[var(--accent-blue)]`
                        : `bg-white/8 border-white/10`
                    } relative border ${
                      selectedContinents.includes(continent)
                        ? "" // No hover effect when already selected
                        : "hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]"
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-[15px] p-2 md:p-2.5 w-11/12`}
                    aria-label={`Select ${continent}`}
                    aria-pressed={selectedContinents.includes(continent)}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-white text-lg md:text-[22px] whitespace-nowrap">
                        {continent}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      
        {/* Countries level */}
        {locationTabViewLevel === 'countries' && viewContinent && showChildButtons && (
          <div className="flex flex-col items-center gap-3 w-full">
            {getAvailableCountries(viewContinent).map((country) => {
              return (
                <button
                  key={country}
                  onClick={() => toggleLocationBadge(country, 'country')}
                  className={`${
                    selectedCountries.includes(country)
                      ? `${GRADIENT_SELECTED} border-[var(--accent-blue)]`
                      : `bg-white/8 border-white/10`
                  } relative border ${
                    selectedCountries.includes(country)
                      ? "" // No hover effect when already selected
                      : "hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]"
                  } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-[15px] p-2 md:p-2.5 w-11/12`}
                  aria-label={`Select ${country}`}
                  aria-pressed={selectedCountries.includes(country)}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-white text-lg md:text-[22px] whitespace-nowrap">
                      {country}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Cities level */}
        {locationTabViewLevel === 'cities' && viewCountry && showChildButtons && (
          <div className="flex flex-col items-center gap-3 w-full">
            {getAvailableCities(viewCountry).map((city) => {
              return (
                <button
                  key={city}
                  onClick={() => toggleLocationBadge(city, 'city')}
                  className={`${
                    selectedCities.includes(city)
                      ? `${GRADIENT_SELECTED} border-[var(--accent-blue)]`
                      : `bg-white/8 border-white/10`
                  } relative border ${
                    selectedCities.includes(city)
                      ? "" // No hover effect when already selected
                      : "hover:shadow-[0_0_15px_rgba(var(--accent-blue-rgb),0.3)]"
                  } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-[15px] p-2 md:p-2.5 w-11/12`}
                  aria-label={`Select ${city}`}
                  aria-pressed={selectedCities.includes(city)}
                >
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-white text-lg md:text-[22px] whitespace-nowrap">
                      {city}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        
      </div>
    </div>
  );
}